import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Select from "../../components/ui/Select";

const STEPS = ["Document Upload","Facial Verification","Review & Submit"];

/* eslint-disable */
export default function KYCPage() {
  const navigate = useNavigate();
  const [step,      setStep]      = useState(0);
  const [docType,   setDocType]   = useState("passport");
  const [uploaded,  setUploaded]  = useState(false);
  const [faceDone,  setFaceDone]  = useState(false);
  const [cameraOn, setCameraOn]   = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [fileDesc, setFileDescription] = useState("Click to upload your "+ docType);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [errCamera, setCameraErr] = useState("");
  const [captured, setCaptured] = useState(false);
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [imgFileName, setImgFileName] = useState("");
  const fpldocument = useRef<HTMLInputElement>(null);
  const [imgUrl, setImageUrl] = useState("");
  const { logout } = useAuth();

  const handleSubmit = async () => {
    setReviewing(true);    
    await api.post("/kyc/review-kyc");
    alert('Your membership has been submitted and will be reviewed for approval. In the mean time, you can login using your phone number and password.');
    logout();
    navigate("/");
  };

  const handleFileUpload = async (e: any) => {  
    if (e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setFileName(e.target.files[0].name);
      setFileDescription("You selected "+ e.target.files[0].name +" for "+ docType);

      if(!e.target.files[0]){
        alert('Select a file to continue!');
        return;
      }
      setFileDescription('Uploading file ...');

      const formData = new FormData();
      formData.append('file', e.target.files[0]);
      formData.append('docType',docType);

      try{
        const response = await api.post("/kyc/document", formData) as any;

        setFileDescription(response.message+' | '+response.fileDetails.originalName +' saved as '+response.fileDetails.savedName);
        if (response.message.includes('success')){
          setUploaded(true);
        }
        
      }
      catch(err: any){
        console.log('Error: '+err.message);
        setFileDescription('No file was uploaded.');
      }
      
    }
  }

  const callFileDialog = () => {
    if (fpldocument.current){
      fpldocument.current.click();      
    }
  }

  const handleDocTypeChange = (e: any) =>{
    const newValue = e;
    updateDocType(newValue);
    updateFileDescripion(newValue);
  }

  const updateDocType = (value: any) =>{
    setDocType(value);
  }

  const updateFileDescripion = (value: any) => {
    setFileDescription("You selected "+ fileName +" for "+ value);
  }

  //Initiate user camera
  const startCamera = async () => {
      try {
        stopCamera();
        setCameraOn(true);

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: 1280, height: 720, facingMode: "user" }, // "user" for front cam
          audio: false
        });
        if(videoRef.current){
          videoRef.current.srcObject = mediaStream;
          setStream(mediaStream);
        }
      } catch (err) {
        setCameraErr("Camera access denied or unavailable.");
        console.error(err);
      }
  };
    

  // Clean up streams when component unmounts
  const stopCamera = () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
  };

  //Capture a single frame from the video stream
  const captureAndUpload = () => {    
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!canvas) {
      console.error("Canvas element not found yet.");
      return;
    }

    if (!video || !canvas) return;

    if (video.readyState < 2) { 
      console.warn("Capture aborted: Video stream is not ready yet.");
      return;
    }

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw current video frame onto canvas
    if (ctx){
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);    
    }
    setCaptured(true);
    //setFaceDone(true);
    stopCamera();

    canvas.toBlob(async (blob) => {
      if (!blob) {
        console.error("The canvas is blank or video stream layer is corrupt.");
        return;
      }
      
      const formData = new FormData();
      formData.append("photo",blob, "captured.jpg");

      try {
        setLoading(true);
        const response = await api.post("/kyc/verify-photo", formData,{headers: {}}) as any;
        const imageName=response.fileDetails.savedName.split('/').pop();
        //const imageName = response.fileDetails.savedName.split('/').pop();        
        setImgFileName(imageName);
        if (response.message.includes('success')){
          setUploaded(true);
          setCaptured(true)
          setFaceDone(true);
          viewPhoto(imageName);
        }
        
      } catch (err) {
        setCameraErr("Upload network error: "+ err);
      } finally {
        setLoading(false);
      }
    }, "image/jpeg", 0.9);

    //uploadPhoto();
  };

  const handleReCapture = () => {
    setCaptured(false);
    startCamera();    
    setCameraOn(true);
  };

  const viewPhoto = (uploadedImgFileMame: string) => {
    const baseUrl = import.meta.env.VITE_BASE_URL;
    
    api.get('/kyc/view-photo',{params: {photoname: uploadedImgFileMame}})
      .then((response) => {
        const imgpath = baseUrl+uploadedImgFileMame;
        setImageUrl(imgpath);
        setLoading(false);
      });
  }


  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="text-4xl">🛡️</span>
          <h1 className="text-xl font-medium text-slate-800 mt-2">Identity Verification</h1>
          <p className="text-slate-500 text-sm mt-1">KYC required to activate financial features</p>
        </div>
        <div className="flex gap-1.5 mb-5">
          {STEPS.map((s, i) => (
            <div key={i} className="flex-1 text-center">
              <div className="h-1 rounded-full mb-1 transition-all duration-300"
                style={{ background: i <= step ? "#059669" : "#e2e8f0" }}/>
              <span className="text-[10px]" style={{ color: i <= step ? "#059669" : "#94a3b8" }}>{s}</span>
            </div>
          ))}
        </div>
        <Card>
          {step===0 && <>
            <h2 className="text-base font-medium text-slate-800 mb-4">Upload ID document</h2>
            <Select label="Document type" value={docType} onChange={(e) => handleDocTypeChange(e)} options={[
              { value:"passport",    label:"International Passport" },
              { value:"national_id", label:"National ID Number (NIN)" },
              { value:"drivers",     label:"Driver's Licence" },
            ]}/>

            <input ref={fpldocument} type="file" onChange={(e) => handleFileUpload(e)} style={{display: 'none'}} />
            <div onClick={() => callFileDialog()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                uploaded ? "border-emerald-400 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:border-emerald-300"}`}>
              
              <div className="text-3xl mb-2">{uploaded ? "✅" : "📄"}</div>
              <p className="text-sm" style={{ color: uploaded ? "#059669" : "#94a3b8" }}>
                {uploaded ? "Document uploaded successfully" : fileDesc}
              </p>
            </div>
            <Button onClick={() => setStep(1)} size="lg" className="w-full justify-center mt-4" disabled={!uploaded}>
              Next →
            </Button>
          </>}

          {step===1 && <>
            <h2 className="text-base font-medium text-slate-800 mb-4">Facial verification -- {cameraOn? '' : <i>Click on the image to start camera</i>}{captured ? (<span onClick={handleReCapture} style={{color:"green", fontWeight:"600"}}>"Click Here to Re-Capture"</span>) : ""}</h2>
            <div className="bg-slate-900 rounded-xl h-48 flex items-center justify-center mb-4">
              {faceDone ? (
                <div className="text-center">
                  {/* <img src={captured? imgUrl : ""} alt="Captured photo" style={{ maxWidth: parent, height: parent, borderRadius:'8px'}} /> */}
                  <img src={captured? imgUrl : ""} alt="Captured photo" style={{ maxWidth: '100%', height: 'auto', borderRadius:'8px'}} />
                  {/* <div className="text-5xl">✅</div> */}
                  <p className="text-emerald-400 text-sm mt-2 font-medium">Face Captured </p>                
                </div>
              ) : (
                <div className="text-center">
                  
                   {!cameraOn ? <div className="w-20 h-20 rounded-full border-2 border-emerald-400 flex items-center justify-center text-4xl mx-auto" onClick={startCamera}>👤</div> : 
                    <div> 
                      
                        <div style={{ position: "relative", display: !captured? 'inline-block' : 'none' }}>
                          {/* Real-time camera preview element */}
                          <video 
                            ref={videoRef} 
                            autoPlay 
                            playsInline 
                            style={{ width: "100%", maxWidth: "500px", borderRadius: "8px", transform: "scaleX(-1)" }} // Mirror view for natural interaction
                          />
                          <Button onClick={captureAndUpload} className="w-full justify-center mb-3">📸 Capture & Verify</Button>
                        </div>

                        <div style={{ position: "relative", maxHeight: "250px", height: captured? '100%' : '0px'}}>
                          <canvas ref={canvasRef} style={{aspectRatio:"2/3", width: "min(100%, 100vh * 2/3)", display: "{captured? 'block' : 'none'}" }} />
                          <p>{errCamera}</p>                          
                        </div>
                      
                    </div>
                   }
                  <p className="text-slate-400 text-xs mt-3">Camera preview</p>
                </div>
              )}
            </div>
            
            <Button onClick={() => setStep(2)} size="lg" className="w-full justify-center" disabled={!faceDone}>Continue →</Button>
          </>}

          {step===2 && <>
            <h2 className="text-base font-medium text-slate-800 mb-4">Review & submit</h2>
            {[
              { label:"Document",           value:"Passport ✅" },
              { label:"Facial verification",value:"Matched ✅" },
              { label:"Account type",       value:"Savings/Target Savings/Investment" },
              { label:"Status",             value:"Ready for activation" },
            ].map(row => (
              <div key={row.label} className="flex justify-between py-2.5 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-500">{row.label}</span>
                <span className="text-sm font-medium text-slate-800">{row.value}</span>
              </div>
            ))}
            <p className="text-xs text-slate-400 mt-3 mb-4">By submitting you agree to our Terms of Service.</p>
            <Button onClick={handleSubmit} size="lg" className="w-full justify-center" disabled={reviewing}>
              {reviewing ? "Verifying…" : "Submit for Verification"}
            </Button>
          </>}
        </Card>
      </div>
    </div>
  );
}
